import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Rocket, RefreshCw, Mail, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VersionPublisher = () => {
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [curRes, histRes] = await Promise.all([
        axios.get(`${API}/system/version`),
        axios.get(`${API}/system/version/history?limit=25`, config),
      ]);
      setCurrent(curRes.data?.version ? curRes.data : null);
      setHistory(histRes.data || []);
    } catch (err) {
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePublish = async (e) => {
    e.preventDefault();
    const v = version.trim();
    if (!v) {
      toast.error('Version is required');
      return;
    }
    if (!window.confirm(
      `Publish v${v}?\n\nThis will send a notification email to every active employee asking them to refresh their browser.`
    )) return;

    setPublishing(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.post(
        `${API}/system/version`,
        { version: v, notes: notes.trim() || null },
        config,
      );
      toast.success(res.data?.message || `Version v${v} published`);
      setVersion('');
      setNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish version');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="version-publisher">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-indigo-600" />
            Publish New Version
          </CardTitle>
          <CardDescription>
            Sends a &quot;please refresh&quot; email to every active employee and updates the in-app refresh banner.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-indigo-700">Currently published</div>
              <div className="text-lg font-semibold text-indigo-900" data-testid="current-version">
                {current?.version ? `v${current.version}` : '— (none yet)'}
              </div>
              {current?.published_at && (
                <div className="text-xs text-indigo-700/80">
                  Published {new Date(current.published_at).toLocaleString()}
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              data-testid="refresh-versions-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>

          <form onSubmit={handlePublish} className="space-y-4">
            <div>
              <Label htmlFor="version-input">New version number</Label>
              <Input
                id="version-input"
                placeholder="e.g. 1.4.2"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                required
                maxLength={32}
                data-testid="version-input"
              />
              <p className="text-xs text-gray-500 mt-1">Letters, digits, and . - _ + only (max 32 chars).</p>
            </div>
            <div>
              <Label htmlFor="notes-input">Release notes (optional)</Label>
              <Textarea
                id="notes-input"
                placeholder="What's new in this release? Leave blank for the generic refresh notice."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
                rows={3}
                data-testid="notes-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                disabled={publishing || !version.trim()}
                data-testid="publish-version-btn"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Mail className="w-4 h-4 mr-2" />
                {publishing ? 'Publishing…' : 'Publish & Notify Everyone'}
              </Button>
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> Emails send in the background — page returns immediately.
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Release history</CardTitle>
          <CardDescription>Last 25 published versions.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="release-history-empty">
              No releases yet. Publishing your first version above will populate this table.
            </p>
          ) : (
            <Table data-testid="release-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Emails sent</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((r) => (
                  <TableRow key={r.id} data-testid={`release-row-${r.version}`}>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono">v{r.version}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.published_at ? new Date(r.published_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-sm">{r.published_by_name || r.published_by_email || '—'}</TableCell>
                    <TableCell className="text-sm">
                      {r.email_completed_at ? (
                        <span>
                          {r.recipients_count}
                          {r.email_errors_count ? (
                            <span className="text-red-600"> ({r.email_errors_count} failed)</span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="text-amber-600">sending…</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm max-w-[24rem] truncate" title={r.notes || ''}>
                      {r.notes || <span className="text-gray-400">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VersionPublisher;
